import { useTranslation } from 'next-i18next';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { createListCollection } from '@chakra-ui/react';

import isEqual from 'lodash/isEqual';

import { ListingAvailableOptions } from '@nocowanie/noc-api';

import { SortApiMapping } from '@app/enums';
import { useSearch } from '@app/hooks/useSearch';

const DEFAULT_SORTING_KEY = 'ScoreDesc';
const DEFAULT_SORTING_VALUE = 'default';

/**
 * Hook for sorting
 */
export function useSearchSorting({ sortings }: { sortings: ListingAvailableOptions['sortings'] }) {
    const { t } = useTranslation('listing', { keyPrefix: 'sorting' });
    const { filters, updateSearchFilters } = useSearch();
    const [localValue, setLocalValue] = useState<string[]>(
        filters.sort ? [`${filters.sort}`] : [DEFAULT_SORTING_VALUE],
    );

    useEffect(() => {
        const newValue = [`${filters.sort ?? DEFAULT_SORTING_VALUE}`];
        setLocalValue(prev => (isEqual(prev, newValue) ? prev : newValue));
    }, [filters.sort]);

    const collection = useMemo(() => {
        const items = (sortings ?? []).map(sorting => {
            const key =
                sorting === DEFAULT_SORTING_KEY ? DEFAULT_SORTING_VALUE : SortApiMapping[sorting];

            return {
                label: t(`labels.${key}`),
                value: key,
            };
        });

        return createListCollection({ items });
    }, [sortings, t]);

    const onValueChange = useCallback(
        (value: typeof localValue) => {
            setLocalValue(value);

            setTimeout(() => {
                updateSearchFilters(
                    {
                        sort: value[0] === DEFAULT_SORTING_VALUE ? null : value[0],
                    },
                    true,
                );
            }, 0);
        },
        [updateSearchFilters],
    );

    return {
        collection,
        defaultValue: [DEFAULT_SORTING_VALUE],
        value: localValue,
        onValueChange,
    };
}
